<script setup lang="ts">
  import { Checkbox as ArkCheckbox } from '@ark-ui/vue';
  import { computed, useId } from 'vue';
  import FormField from '../FormField/FormField.vue';
  import {
    controlClass,
    indicatorClass,
    labelClass,
    labelMultilineClass,
    rootClass,
    rootFullWidthClass,
    rootMultilineClass,
  } from './Checkbox.css';
  import type {
    CheckboxEmits,
    CheckboxProps,
    CheckboxSlots,
  } from './Checkbox.types';
  import CheckboxIndicator from './CheckboxIndicator/CheckboxIndicator.vue';

  const props = withDefaults(defineProps<CheckboxProps>(), {
    indeterminate: false,
    disabled: false,
    required: false,
    fullWidth: false,
    multiline: false,
  });

  const emit = defineEmits<CheckboxEmits>();
  defineSlots<CheckboxSlots>();

  const autoId = useId();
  const inputId = computed(() => props.id ?? autoId);

  const rootProps = computed(() => ({
    ids: { hiddenInput: inputId.value },
    disabled: props.disabled,
    required: props.required,
    invalid: !!props.error,
    name: props.name,
    value: props.value,
    checked: props.indeterminate
      ? ('indeterminate' as const)
      : props.modelValue,
    onCheckedChange: (details: { checked: boolean | 'indeterminate' }) =>
      emit('update:modelValue', details.checked === true),
  }));
</script>

<template>
  <FormField
    :id="inputId"
    :error="error"
    :announce-warning="false"
    :disabled-reason="disabled ? disabledReason : undefined"
  >
    <template #default="{ describedBy }">
      <ArkCheckbox.Root
        v-bind="rootProps"
        :class="[
          rootClass,
          fullWidth && rootFullWidthClass,
          (multiline || $slots.label) && rootMultilineClass,
        ]"
      >
        <ArkCheckbox.Control :class="controlClass">
          <CheckboxIndicator
            :class="indicatorClass"
            :checked="modelValue"
            :indeterminate="indeterminate"
            :disabled="disabled"
          />
        </ArkCheckbox.Control>
        <ArkCheckbox.Label
          v-if="label || $slots.label"
          :class="[labelClass, (multiline || $slots.label) && labelMultilineClass]"
          :title="multiline || $slots.label ? undefined : label"
        >
          <slot name="label">{{ label }}</slot>
        </ArkCheckbox.Label>
        <ArkCheckbox.HiddenInput
          :aria-label="ariaLabel"
          :aria-describedby="describedBy"
        />
      </ArkCheckbox.Root>
    </template>
  </FormField>
</template>
